"use client";

import type * as React from "react";

import { Checkbox as AriaCheckbox, type CheckboxProps as AriaCheckboxProps } from "react-aria-components";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@ui/lib/utils";

const checkboxVariants = cva(
  [
    "tw-peer tw-shrink-0 tw-border tw-border-gray-300 tw-bg-transparent tw-transition-colors tw-cursor-pointer",
    "tw-flex tw-items-center tw-justify-center",
    "focus-visible:tw-outline-focus",
    "data-[disabled]:tw-cursor-not-allowed data-[disabled]:tw-opacity-50",
    "data-[selected]:tw-border-black data-[selected]:tw-bg-black data-[selected]:tw-text-white",
  ],
  {
    variants: {
      size: {
        sm: "tw-size-4 tw-rounded",
        md: "tw-size-5 tw-rounded-md",
        lg: "tw-size-7 tw-rounded-lg",
      },
    },
    defaultVariants: {
      size: "sm",
    },
  },
);

const checkIconVariants = cva("", {
  variants: {
    size: {
      sm: "tw-size-2.5",
      md: "tw-size-3",
      lg: "tw-size-5",
    },
  },
  defaultVariants: {
    size: "sm",
  },
});

interface CheckboxProps extends Omit<AriaCheckboxProps, "children">, VariantProps<typeof checkboxVariants> {
  children?: React.ReactNode;
  wrapperClassName?: string;
  /** @deprecated Use `isSelected` instead */
  checked?: boolean;
  /** @deprecated Use `onChange` instead */
  onCheckedChange?: (checked: boolean) => void;
}

const Checkbox = ({
  className,
  wrapperClassName,
  size,
  children,
  checked,
  onCheckedChange,
  onChange,
  isSelected,
  ...props
}: CheckboxProps) => {
  // Support legacy Radix props for backwards compatibility
  const selected = isSelected ?? checked;
  const handleChange = (newValue: boolean) => {
    onChange?.(newValue);
    onCheckedChange?.(newValue);
  };

  return (
    <AriaCheckbox
      className={cn(
        "tw-group tw-flex tw-items-center tw-gap-2 [-webkit-tap-highlight-color:transparent]",
        children ? "" : checkboxVariants({ size }),
        children ? "" : className,
        wrapperClassName,
      )}
      isSelected={selected}
      onChange={handleChange}
      {...props}
    >
      {({ isSelected: currentlySelected, isIndeterminate }) => (
        <>
          {children !== undefined && (
            <div
              className={cn(checkboxVariants({ size }), className)}
              data-selected={currentlySelected || isIndeterminate || undefined}
              data-indeterminate={isIndeterminate || undefined}
            >
              {(currentlySelected || isIndeterminate) && (
                <svg
                  className={cn(checkIconVariants({ size }), "tw-text-current")}
                  fill='none'
                  stroke={isIndeterminate ? "none" : "currentColor"}
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={3}
                  viewBox='0 0 24 24'
                  aria-hidden='true'
                >
                  {isIndeterminate ? (
                    <rect x={2} y={10} width={20} height={4} fill='currentColor' />
                  ) : (
                    <path d='M20 6 9 17l-5-5' />
                  )}
                </svg>
              )}
            </div>
          )}
          {children === undefined && (currentlySelected || isIndeterminate) && (
            <svg
              className={cn(checkIconVariants({ size }), "tw-text-current")}
              fill='none'
              stroke={isIndeterminate ? "none" : "currentColor"}
              strokeLinecap='round'
              strokeLinejoin='round'
              strokeWidth={3}
              viewBox='0 0 24 24'
              aria-hidden='true'
            >
              {isIndeterminate ? (
                <rect x={2} y={10} width={20} height={4} fill='currentColor' />
              ) : (
                <path d='M20 6 9 17l-5-5' />
              )}
            </svg>
          )}
          {children}
        </>
      )}
    </AriaCheckbox>
  );
};

Checkbox.displayName = "Checkbox";

export { Checkbox, checkboxVariants };
export type { CheckboxProps };
